/**
 * ReceiptScanner — snap a grocery receipt, AI-extract line items,
 * review + submit, earn contribution points.
 *
 * Routes:
 *   /food-pulse/receipts/new    — capture flow
 *   /food-pulse/receipts/mine   — receipt history + points
 *
 * Steps (state machine):
 *   pick     → user picks / snaps a photo
 *   upload   → POST /api/receipts/upload
 *   parse    → POST /api/receipts/{id}/parse   (AI vision)
 *   review   → user edits line items
 *   confirm  → POST /api/receipts/{id}/confirm → points awarded
 *   done     → thanks screen
 *
 * Graceful degradation: if AI parse fails or budget is hit, the user
 * lands in the review screen with an empty grid and can add lines
 * manually.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Camera, Upload, Loader2, Check, X, Plus, Trash2, Store,
  Calendar, DollarSign, MapPin, Sparkles, AlertTriangle, Award, Receipt,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import LocationPicker, { getStoredLocation } from "@/components/LocationPicker";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";
const RED   = "#7B2A2E";

const UNITS = ["each", "kg", "g", "lb", "oz", "litre", "ml", "dozen", "pack", "box"];

const VERTICALS = [
  { key: "grocery",    label: "Grocery",       hint: "Auto-adds to pantry",     icon: "🛒" },
  { key: "restaurant", label: "Restaurant",    hint: "Dining out",              icon: "🍽️" },
  { key: "cafe",       label: "Café",          hint: "Coffee, bakery",          icon: "☕" },
  { key: "farmers",    label: "Farmers market",hint: "Adds to pantry",          icon: "🥬" },
  { key: "retail",     label: "Retail",        hint: "Non-food shopping",       icon: "🛍️" },
  { key: "pharmacy",   label: "Pharmacy",      hint: "Medicine, personal care", icon: "💊" },
  { key: "fuel",       label: "Fuel",          hint: "Gas + convenience",       icon: "⛽" },
  { key: "other",      label: "Other",         hint: "Anything else",           icon: "🧾" },
];

/* ================================================================
   Scanner page
   ================================================================ */
export default function ReceiptScanner() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [phase, setPhase]     = useState("vertical"); // vertical | pick | upload | parse | review | done
  const [vertical, setVertical] = useState("grocery");
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [pantryAdded, setPantryAdded] = useState(0);
  const [aiWarning, setAiWarning] = useState(null);

  // review-mode editable state
  const [store, setStore]     = useState("");
  const [date,  setDate]      = useState("");
  const [total, setTotal]     = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [tax, setTax]         = useState("");
  const [items, setItems]     = useState([]);
  const [location, setLocation] = useState(getStoredLocation());

  const fileRef   = useRef(null);
  const cameraRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type)) return toast.error("Please pick an image");
    setFile(f);
    const r = new FileReader();
    r.onload = () => setPreview(r.result);
    r.readAsDataURL(f);
  };

  const uploadAndParse = useCallback(async () => {
    if (!file) return;
    setBusy(true); setPhase("upload"); setAiWarning(null);
    try {
      // 1) upload
      const form = new FormData();
      form.append("file", file);
      form.append("vertical", vertical);
      const up = await http.post("/receipts/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rec = up.data;
      setReceipt(rec);
      // 2) parse
      setPhase("parse");
      let parsed = null;
      try {
        const pr = await http.post(`/receipts/${rec.id}/parse`);
        parsed = pr.data;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 429) {
          setAiWarning("AI daily limit hit — you can still add line items manually below.");
          parsed = { ...rec, status: "awaiting-parse", line_items: [] };
        } else {
          setAiWarning(e?.response?.data?.detail || "AI parse failed — enter items manually.");
          parsed = { ...rec, status: "parse-failed", line_items: [] };
        }
      }
      setReceipt(parsed);
      // 3) hydrate review form
      const ext = parsed.extracted || {};
      setStore(ext.store_name || "");
      setDate(ext.receipt_date || "");
      setTotal(ext.total ?? "");
      setSubtotal(ext.subtotal ?? "");
      setTax(ext.tax ?? "");
      setItems(parsed.line_items || []);
      setPhase("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      setPhase("pick");
    } finally {
      setBusy(false);
    }
  }, [file]);

  const updateItem = (idx, patch) =>
    setItems((p) => p.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));
  const addItem = () => setItems((p) => [...p, {
    name: "", brand: "", quantity: 1, unit: "each", unit_price: "", total_price: 0,
  }]);

  const confirmReceipt = async () => {
    if (!receipt?.id) return;
    if (!items.length) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      const r = await http.post(`/receipts/${receipt.id}/confirm`, {
        store_name:   store.trim() || undefined,
        receipt_date: date || undefined,
        subtotal:     subtotal !== "" ? Number(subtotal) : undefined,
        tax:          tax !== "" ? Number(tax) : undefined,
        total:        total !== "" ? Number(total) : undefined,
        currency:     "USD",
        city:         location?.city,
        state:        location?.state,
        country:      location?.country,
        lat:          location?.latitude,
        lon:          location?.longitude,
        line_items:   items.map((it) => ({
          raw:          it.raw,
          name:         it.name,
          brand:        it.brand || undefined,
          quantity:     it.quantity ? Number(it.quantity) : undefined,
          unit:         it.unit || undefined,
          unit_price:   it.unit_price !== "" && it.unit_price != null ? Number(it.unit_price) : undefined,
          total_price:  Number(it.total_price || 0),
          matched_slug: it.matched_slug || undefined,
          category_hint: it.category_hint || undefined,
        })),
      });
      setPointsEarned(r.data?.points_awarded || 0);
      setPantryAdded(r.data?.pantry_added || 0);
      setPhase("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Receipt className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-2">Sign in to scan receipts</h1>
          <p className="opacity-80 mb-6">Earn contribution points every time you snap and confirm a grocery receipt.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: CREAM, color: GREEN }} data-testid="receipt-scanner">
      <SeoHead section="food-pulse-receipts" title="Snap a receipt — Food Pulse"
               description="Snap your grocery receipt, verify prices, earn contribution points." />

      {/* App-bar */}
      <div className="sticky top-0 z-30 backdrop-blur bg-emerald-900 text-white">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="receipt-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="font-display text-lg flex items-center gap-2">
            <Receipt className="w-4 h-4" /> Snap Receipt
          </div>
          <Link to="/food-pulse/receipts/mine" className="ml-auto text-xs opacity-80 hover:opacity-100 underline"
                data-testid="receipt-history-link">
            History →
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        <StepPill phase={phase} vertical={vertical} />

        {/* VERTICAL --------------------------------------------- */}
        {phase === "vertical" && (
          <div className="rounded-3xl bg-white border-2 p-6" style={{ borderColor: "#E9D8B4" }}
               data-testid="receipt-vertical">
            <div className="font-display text-2xl mb-1" style={{ color: GREEN }}>What kind of receipt?</div>
            <p className="text-sm opacity-70 mb-4">
              This picks the right AI extractor. Grocery + farmers market receipts auto-add to your pantry.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {VERTICALS.map((v) => {
                const on = vertical === v.key;
                return (
                  <button key={v.key} type="button" onClick={() => setVertical(v.key)}
                          className={`rounded-2xl border-2 p-3 text-center transition`}
                          style={{
                            background: on ? "#FFEDD5" : "white",
                            borderColor: on ? AMBER : "#E9D8B4",
                          }}
                          data-testid={`receipt-vertical-${v.key}`}>
                    <div className="text-2xl mb-0.5">{v.icon}</div>
                    <div className="text-xs font-semibold" style={{ color: GREEN }}>{v.label}</div>
                    <div className="text-[10px] opacity-60">{v.hint}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex justify-end">
              <button onClick={() => setPhase("pick")}
                      className="rounded-full px-5 py-2 text-sm font-semibold text-white flex items-center gap-2"
                      style={{ background: GREEN }}
                      data-testid="receipt-vertical-next">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* PICK -------------------------------------------------- */}
        {phase === "pick" && (
          <div className="rounded-3xl border-2 border-dashed p-8 text-center bg-white"
               style={{ borderColor: AMBER }} data-testid="receipt-pick">
            {preview ? (
              <div>
                <img src={preview} className="max-h-72 mx-auto rounded-2xl object-contain shadow"
                     alt="Receipt preview" />
                <div className="mt-4 flex justify-center gap-2 flex-wrap">
                  <button onClick={() => { setFile(null); setPreview(null); }}
                          className="rounded-full px-4 py-2 text-sm font-semibold border"
                          style={{ borderColor: GREEN, color: GREEN }}
                          data-testid="receipt-repick">
                    Retake
                  </button>
                  <button onClick={uploadAndParse} disabled={busy}
                          className="rounded-full px-5 py-2 text-sm font-semibold text-white flex items-center gap-2"
                          style={{ background: GREEN }}
                          data-testid="receipt-upload-parse">
                    <Sparkles className="w-4 h-4" /> Upload & AI-extract
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Receipt className="w-14 h-14 mx-auto mb-3 opacity-60" style={{ color: AMBER }} />
                <div className="font-display text-2xl mb-1">Snap your receipt</div>
                <p className="text-sm opacity-70 max-w-md mx-auto mb-5">
                  Point at a grocery / supermarket receipt. We'll AI-extract the store,
                  date, items and prices — you review and confirm.
                </p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <button onClick={() => cameraRef.current?.click()}
                          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white flex items-center gap-2"
                          style={{ background: GREEN }}
                          data-testid="receipt-camera">
                    <Camera className="w-4 h-4" /> Take photo
                  </button>
                  <button onClick={() => fileRef.current?.click()}
                          className="rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 border-2"
                          style={{ borderColor: GREEN, color: GREEN, background: "white" }}
                          data-testid="receipt-upload-btn">
                    <Upload className="w-4 h-4" /> Upload
                  </button>
                </div>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={(e) => pickFile(e.target.files?.[0])}
                       data-testid="receipt-camera-input" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => pickFile(e.target.files?.[0])}
                       data-testid="receipt-file-input" />
                <div className="mt-6 grid grid-cols-3 gap-3 max-w-md mx-auto text-xs">
                  <Bullet n="+2" label="Upload" />
                  <Bullet n="+5" label="Confirm" />
                  <Bullet n="+1" label="per matched line" />
                </div>
              </>
            )}
          </div>
        )}

        {/* UPLOAD/PARSE -------------------------------------------- */}
        {(phase === "upload" || phase === "parse") && (
          <div className="rounded-3xl bg-white p-8 text-center" data-testid="receipt-progress">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: AMBER }} />
            <div className="font-display text-xl mb-1">
              {phase === "upload" ? "Uploading your receipt…" : "AI reading the receipt…"}
            </div>
            <div className="text-sm opacity-70">This usually takes a few seconds.</div>
          </div>
        )}

        {/* REVIEW --------------------------------------------------- */}
        {phase === "review" && (
          <>
            {aiWarning && (
              <div className="rounded-xl p-3 flex items-start gap-2"
                   style={{ background: "#FEF3C7", color: "#78350F" }} data-testid="receipt-ai-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="text-sm">{aiWarning}</div>
              </div>
            )}

            <div className="rounded-2xl bg-white border p-4 space-y-3" style={{ borderColor: "#E9D8B4" }}>
              <div className="font-semibold text-lg">Receipt details</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Store" icon={Store}>
                  <input value={store} onChange={(e) => setStore(e.target.value)}
                         placeholder="e.g. Trader Joe's"
                         className="w-full outline-none bg-transparent text-sm"
                         data-testid="receipt-store" />
                </Field>
                <Field label="Date" icon={Calendar}>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                         className="w-full outline-none bg-transparent text-sm"
                         data-testid="receipt-date" />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Subtotal" icon={DollarSign}>
                  <input type="number" step="0.01" value={subtotal}
                         onChange={(e) => setSubtotal(e.target.value)}
                         className="w-full outline-none bg-transparent text-sm"
                         data-testid="receipt-subtotal" />
                </Field>
                <Field label="Tax" icon={DollarSign}>
                  <input type="number" step="0.01" value={tax}
                         onChange={(e) => setTax(e.target.value)}
                         className="w-full outline-none bg-transparent text-sm"
                         data-testid="receipt-tax" />
                </Field>
                <Field label="Total" icon={DollarSign}>
                  <input type="number" step="0.01" value={total}
                         onChange={(e) => setTotal(e.target.value)}
                         className="w-full outline-none bg-transparent text-sm font-bold"
                         data-testid="receipt-total" />
                </Field>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Store location
                </div>
                <LocationPicker value={location} onChange={setLocation} compact />
              </div>
            </div>

            {/* Line items --------------------------------- */}
            <div className="rounded-2xl bg-white border p-4" style={{ borderColor: "#E9D8B4" }}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">Line items ({items.length})</div>
                <button onClick={addItem}
                        className="rounded-full px-3 py-1 text-xs font-semibold border-2 flex items-center gap-1"
                        style={{ borderColor: GREEN, color: GREEN }}
                        data-testid="receipt-add-item">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {items.length === 0 ? (
                <div className="text-sm opacity-60 text-center py-6">
                  No line items yet. Tap “Add” to enter one manually.
                </div>
              ) : (
                <div className="space-y-2" data-testid="receipt-items">
                  {items.map((it, i) => (
                    <LineItemRow key={i} idx={i} item={it}
                                 onChange={(patch) => updateItem(i, patch)}
                                 onRemove={() => removeItem(i)} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* DONE ---------------------------------------------------- */}
        {phase === "done" && (
          <div className="rounded-3xl bg-white p-8 text-center border-2" style={{ borderColor: AMBER }}
               data-testid="receipt-done">
            <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
                 style={{ background: "#DCFCE7" }}>
              <Check className="w-8 h-8" style={{ color: GREEN }} />
            </div>
            <div className="font-display text-3xl mb-1" style={{ color: GREEN }}>Thanks!</div>
            <p className="text-sm opacity-80 mb-4">Your receipt is with our moderators. Every verified line makes FoodPulse's price data smarter.</p>
            <div className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold"
                 style={{ background: "#FFEDD5", color: RED }}>
              <Award className="w-4 h-4" /> +{pointsEarned} contribution points
            </div>
            {pantryAdded > 0 && (
              <div className="mt-3 text-sm" style={{ color: GREEN }}>
                <span className="font-semibold">{pantryAdded}</span> item{pantryAdded === 1 ? "" : "s"} auto-added to your Pantry with estimated expiry dates.
                <Link to="/food-pulse/my-lists?tab=pantry" className="ml-1 underline font-semibold">View pantry →</Link>
              </div>
            )}
            <div className="mt-5 flex justify-center gap-2 flex-wrap">
              <Link to="/food-pulse/household"
                    className="rounded-full px-4 py-2 text-sm font-semibold text-white"
                    style={{ background: AMBER, color: GREEN }}
                    data-testid="receipt-done-dashboard">
                Household dashboard →
              </Link>
              <Link to="/food-pulse/receipts/mine"
                    className="rounded-full px-4 py-2 text-sm font-semibold border-2"
                    style={{ borderColor: GREEN, color: GREEN }}>
                My receipts
              </Link>
              <button onClick={() => { setFile(null); setPreview(null); setReceipt(null);
                                        setItems([]); setStore(""); setDate(""); setTotal("");
                                        setSubtotal(""); setTax(""); setPhase("vertical");
                                        setPointsEarned(0); setPantryAdded(0); }}
                      className="rounded-full px-5 py-2 text-sm font-semibold text-white"
                      style={{ background: GREEN }}
                      data-testid="receipt-scan-another">
                Scan another
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky confirm bar ------------------------------------- */}
      {phase === "review" && (
        <div className="fixed bottom-0 left-0 right-0 border-t backdrop-blur bg-white/95 z-40" style={{ borderColor: "#E9D8B4" }}>
          <div className="max-w-3xl mx-auto px-4 py-3 flex gap-3">
            <button onClick={() => { setFile(null); setPreview(null); setReceipt(null); setPhase("pick"); }}
                    className="rounded-full px-4 py-3 font-semibold border" style={{ borderColor: GREEN, color: GREEN }}>
              Discard
            </button>
            <button onClick={confirmReceipt} disabled={busy || !items.length}
                    className="flex-1 rounded-full px-5 py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: GREEN }}
                    data-testid="receipt-confirm">
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                : <><Check className="w-4 h-4" /> Confirm receipt · earn points</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   Sub-components
   ================================================================ */
function StepPill({ phase, vertical }) {
  const v = VERTICALS.find((x) => x.key === vertical);
  const map = {
    vertical: { label: "1 · What kind of receipt", pct: 15 },
    pick:     { label: "2 · Pick a photo",         pct: 30 },
    upload:   { label: "3 · Uploading…",           pct: 50 },
    parse:    { label: "4 · AI reading…",          pct: 70 },
    review:   { label: "5 · Review & submit",      pct: 88 },
    done:     { label: "Done!",                    pct: 100 },
  };
  const s = map[phase] || map.vertical;
  return (
    <div className="rounded-full px-4 py-2 bg-white border flex items-center gap-3 text-xs font-mono uppercase tracking-widest"
         style={{ borderColor: "#E9D8B4" }} data-testid="receipt-step">
      <span style={{ color: GREEN }}>{s.label}</span>
      {v && phase !== "vertical" && (
        <span className="rounded-full px-2 py-0.5 text-[10px] flex items-center gap-1"
              style={{ background: "#FFEDD5", color: GREEN }}>
          {v.icon} {v.label}
        </span>
      )}
      <div className="flex-1 h-1 rounded-full bg-black/10 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${s.pct}%`, background: AMBER }} />
      </div>
    </div>
  );
}

function Field({ label, icon: Ico, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-0.5">{label}</div>
      <div className="flex items-center gap-2 rounded-xl border px-3 py-1.5" style={{ borderColor: "#E9D8B4" }}>
        {Ico && <Ico className="w-3.5 h-3.5 opacity-60 flex-shrink-0" />}
        {children}
      </div>
    </div>
  );
}

function Bullet({ n, label }) {
  return (
    <div className="rounded-xl p-2 text-center" style={{ background: "#FFEDD5" }}>
      <div className="font-display text-lg font-bold" style={{ color: GREEN }}>{n}</div>
      <div className="text-[10px] opacity-70">{label}</div>
    </div>
  );
}

function LineItemRow({ idx, item, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-12 gap-1.5 items-center bg-amber-50/40 rounded-xl p-2"
         data-testid={`receipt-item-${idx}`}>
      <input value={item.name || ""} onChange={(e) => onChange({ name: e.target.value })}
             placeholder="Product name"
             className="col-span-4 rounded-lg border px-2 py-1 text-sm outline-none"
             style={{ borderColor: "#E9D8B4" }}
             data-testid={`receipt-item-name-${idx}`} />
      <input value={item.brand || ""} onChange={(e) => onChange({ brand: e.target.value })}
             placeholder="Brand"
             className="col-span-2 rounded-lg border px-2 py-1 text-xs outline-none"
             style={{ borderColor: "#E9D8B4" }} />
      <input type="number" step="0.01" min="0" value={item.quantity ?? ""}
             onChange={(e) => onChange({ quantity: e.target.value })}
             placeholder="Qty"
             className="col-span-1 rounded-lg border px-2 py-1 text-xs outline-none"
             style={{ borderColor: "#E9D8B4" }} />
      <select value={item.unit || "each"} onChange={(e) => onChange({ unit: e.target.value })}
              className="col-span-2 rounded-lg border px-2 py-1 text-xs outline-none bg-white"
              style={{ borderColor: "#E9D8B4" }}>
        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
      <input type="number" step="0.01" min="0" value={item.total_price ?? ""}
             onChange={(e) => onChange({ total_price: e.target.value })}
             placeholder="Price"
             className="col-span-2 rounded-lg border px-2 py-1 text-sm font-semibold outline-none"
             style={{ borderColor: "#E9D8B4" }}
             data-testid={`receipt-item-price-${idx}`} />
      <button onClick={onRemove}
              className="col-span-1 rounded-lg p-1 text-red-700 hover:bg-red-50 flex items-center justify-center"
              data-testid={`receipt-item-remove-${idx}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      {item.matched_slug && (
        <div className="col-span-12 text-[10px] opacity-70 flex items-center gap-1">
          <Check className="w-3 h-3" style={{ color: GREEN }} />
          matched: <Link to={`/food-pulse/food/${item.matched_slug}`} className="underline font-semibold">
            {item.matched_name || item.matched_slug}
          </Link>
        </div>
      )}
    </div>
  );
}
